import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, FloppyDisk, Plus, Trash, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import RichTextEditor from "../components/RichTextEditor";

const TABS = [
  ["generali", "Generali"],
  ["impostazioni", "Impostazioni"],
  ["landing", "Landing page"],
  ["thankyou", "Thank you page"],
  ["caparra", "Caparra"],
  ["orari", "Orari"],
];

const FIELD_DEFS = [
  ["nome", "Nome", true], ["cognome", "Cognome", false], ["telefono", "Telefono", true],
  ["email", "Email", false], ["data_nascita", "Data di nascita", false],
  ["indirizzo", "Indirizzo", false], ["citta", "Città", false], ["cap", "CAP", false],
  ["provincia", "Provincia", false], ["nazione", "Nazione", false],
];

const WEEKDAYS = [["lun", "Lunedì"], ["mar", "Martedì"], ["mer", "Mercoledì"], ["gio", "Giovedì"],
                  ["ven", "Venerdì"], ["sab", "Sabato"], ["dom", "Domenica"]];

const DEFAULT_FORM = {
  name: "", tipo: "standard", active: true, periods: [],
  orari_mode: "default", orari_custom: {},
  tempo_limite_ore: 1, max_giorni_futuri: 15,
  pax_min: 1, pax_max: 50,
  on_max_pax: "avviso", max_pax_message: "",
  slot_capacity: 0, slot_full: "nascondi",
  no_availability: "standard", no_availability_message: "",
  waitlist: false, etichetta: "", tag_name: "",
  fields: {
    nome: { on: true, req: true }, cognome: { on: true, req: true },
    telefono: { on: true, req: true }, email: { on: true, req: false },
  },
  landing: { titolo: "", sottotitolo: "", descrizione: "", condizioni: "", immagine: "", immagine_mode: "banner", img_fit: "copri",
    solo_landing: false, cta_links: [{ label: "", url: "" }, { label: "", url: "" }] },
  thankyou: { titolo: "", descrizione: "" },
  caparra: { attivo: false, importo: "", note: "" },
  coupon_id: "", coupon_mode: "emetti", coupon_rules: [], promotion_id: "", room_id: "",
};

const inputCls = "w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";
const labelCls = "block text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";
const areaCls = "w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900";

export default function ModuloEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === "nuovo";
  const [tab, setTab] = useState(searchParams.get("tab") || "generali");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [tags, setTags] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [promos, setPromos] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [newTag, setNewTag] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (k, sub, v) => setForm((f) => ({ ...f, [k]: { ...(f[k] || {}), ...v !== undefined && typeof v === "object" && !Array.isArray(v) && sub === null ? v : { [sub]: v } } }));
  const setField = (key, prop, v) => setForm((f) => ({
    ...f, fields: { ...f.fields, [key]: { on: false, req: false, ...(f.fields || {})[key], [prop]: v } },
  }));

  const fileRef = useRef(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const uploadLandingImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)"); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/marketing/uploads/image", fd);
      setNested("landing", "immagine", r.data.url);
      toast.success("Immagine caricata");
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingImg(false); }
  };

  useEffect(() => {
    api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    api.get("/promotions").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    if (!isNew) {
      api.get(`/web-forms/${id}`).then((r) => {
        const d = r.data;
        setForm({ ...DEFAULT_FORM, ...d, fields: { ...DEFAULT_FORM.fields, ...(d.fields || {}) },
                  landing: { ...DEFAULT_FORM.landing, ...(d.landing || {}) },
                  thankyou: { ...DEFAULT_FORM.thankyou, ...(d.thankyou || {}) },
                  caparra: { ...DEFAULT_FORM.caparra, ...(d.caparra || {}) },
                  coupon_id: d.coupon_id || "", coupon_mode: d.coupon_mode || "emetti",
                  coupon_rules: d.coupon_rules || [],
                  promotion_id: d.promotion_id || "", room_id: d.room_id || "" });
      }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    }
  }, [id, isNew]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome del modulo"); setTab("generali"); return; }
    setSaving(true);
    try {
      const payload = { ...form,
        coupon_id: form.coupon_id || null, promotion_id: form.promotion_id || null, room_id: form.room_id || null,
        pax_min: parseInt(form.pax_min) || 1, pax_max: parseInt(form.pax_max) || 50,
        tempo_limite_ore: parseInt(form.tempo_limite_ore) || 0,
        max_giorni_futuri: parseInt(form.max_giorni_futuri) || 0,
        slot_capacity: parseInt(form.slot_capacity) || 0 };
      if (isNew) {
        const r = await api.post("/web-forms", payload);
        toast.success("Modulo creato");
        navigate(`/moduli/${r.data.id}`, { replace: true });
      } else {
        await api.patch(`/web-forms/${id}`, payload);
        toast.success("Modulo salvato");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const createTag = async () => {
    const name = newTag.trim();
    if (!name) return;
    try {
      await api.post("/tags", { name });
      const r = await api.get("/tags");
      setTags(r.data);
      set("tag_name", name);
      setNewTag("");
      toast.success(`Tag "${name}" creato e selezionato`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setTurno = (day, idx, k, v) => setForm((f) => {
    const oc = { ...(f.orari_custom || {}) };
    const d = { ...(oc[day] || { aperto: true, turni: [] }) };
    const turni = [...(d.turni || [])];
    turni[idx] = { ...turni[idx], [k]: k === "slot_minuti" ? (parseInt(v) || 30) : v };
    d.turni = turni;
    oc[day] = d;
    return { ...f, orari_custom: oc };
  });

  if (loading) return <div className="text-sm text-zinc-400 py-10 text-center">Caricamento...</div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="modulo-editor">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/moduli")} data-testid="editor-back"
          className="h-10 w-10 rounded-md bg-white border border-zinc-300 flex items-center justify-center text-zinc-600 hover:border-zinc-400">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">
            {isNew ? "Nuovo modulo" : `Modifica modulo${form.name ? ` — ${form.name}` : ""}`}
          </h1>
          {!isNew && form.slug && <p className="text-xs text-zinc-500 font-mono">/p/…/m/{form.slug}</p>}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-zinc-200" data-testid="editor-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`shrink-0 px-3 py-2 text-xs font-semibold border-b-2 transition ${tab === k ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "generali" && (
        <div className="space-y-4" data-testid="panel-generali">
          <div><label className={labelCls}>Nome *</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="f-nome-modulo" /></div>
          <div>
            <label className={labelCls}>Tipologia *</label>
            <div className="flex gap-2">
              {[["standard", "Standard"], ["evento", "Evento"], ["coda", "Coda (walk-in)"]].map(([k, l]) => (
                <button type="button" key={k} onClick={() => set("tipo", k)} data-testid={`f-tipo-${k}`}
                  className={`h-10 px-5 rounded-md border text-sm font-semibold transition ${form.tipo === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                  {l}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">
              Standard = sempre disponibile; Evento = legato a periodi/date specifiche;
              Coda = il cliente si mette in fila senza scegliere data/orario e vede la sua posizione.
            </p>
          </div>
          <div>
            <label className={labelCls}>Periodi di validità (vuoto = sempre attivo)</label>
            <div className="space-y-2">
              {(form.periods || []).map((p, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`f-periodo-${i}`}>
                  {p._fissa ? (<>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 shrink-0">Data fissa</span>
                    <input type="date" value={p.da} onChange={(e) => set("periods", form.periods.map((x, j) => j === i ? { ...x, da: e.target.value, a: e.target.value } : x))}
                      className={inputCls} data-testid={`f-periodo-fissa-${i}`} />
                  </>) : (<>
                    <input type="date" value={p.da} onChange={(e) => set("periods", form.periods.map((x, j) => j === i ? { ...x, da: e.target.value } : x))} className={inputCls} />
                    <span className="text-xs text-zinc-400">→</span>
                    <input type="date" value={p.a} onChange={(e) => set("periods", form.periods.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} className={inputCls} />
                  </>)}
                  <button type="button" onClick={() => set("periods", form.periods.filter((_, j) => j !== i))} data-testid={`f-periodo-del-${i}`}
                    className="h-10 w-10 shrink-0 rounded-md bg-white border border-rose-200 text-rose-600 flex items-center justify-center"><Trash size={14} /></button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => set("periods", [...(form.periods || []), { da: "", a: "" }])} data-testid="f-periodo-add"
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                  <Plus size={13} /> Aggiungi periodo
                </button>
                <button type="button" onClick={() => set("periods", [...(form.periods || []), { da: "", a: "", _fissa: true }])} data-testid="f-periodo-add-fissa"
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
                  <Plus size={13} /> Aggiungi data fissa
                </button>
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Campi anagrafica *</label>
            <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="f-campi">
              {FIELD_DEFS.map(([key, label, locked]) => {
                const c = (form.fields || {})[key] || { on: false, req: false };
                const on = locked || c.on;
                const req = locked || c.req;
                return (
                  <div key={key} className="flex items-center gap-4 px-3 py-2" data-testid={`f-campo-${key}`}>
                    <label className="flex items-center gap-2 text-sm text-zinc-800 flex-1">
                      <input type="checkbox" checked={on} disabled={locked}
                        onChange={(e) => setField(key, "on", e.target.checked)} data-testid={`f-campo-${key}-on`}
                        className="h-4 w-4 accent-zinc-900" />
                      {label} {locked && <span className="text-[10px] text-zinc-400">(sempre attivo)</span>}
                    </label>
                    <label className={`flex items-center gap-2 text-xs ${on && !locked ? "text-zinc-700" : "text-zinc-300"}`}>
                      <input type="checkbox" checked={req} disabled={locked || !on}
                        onChange={(e) => setField(key, "req", e.target.checked)} data-testid={`f-campo-${key}-req`}
                        className="h-4 w-4 accent-zinc-900" />
                      Obbligatorio
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <label className={labelCls}>Sala di destinazione (opzionale)</label>
            <select value={form.room_id} onChange={(e) => set("room_id", e.target.value)} className={inputCls} data-testid="f-sala">
              <option value="">Nessuna sala pre-assegnata</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
      )}

      {tab === "impostazioni" && (
        <div className="space-y-4" data-testid="panel-impostazioni">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Tempo limite prenotazione (ore prima) *</label>
              <input type="number" min="0" value={form.tempo_limite_ore} onChange={(e) => set("tempo_limite_ore", e.target.value)} className={inputCls} data-testid="f-tempo-limite" /></div>
          {form.tipo === "coda" && (
            <div><label className={labelCls}>Attesa media per gruppo (minuti) — stima mostrata al cliente</label>
              <input type="number" min="1" max="120" value={form.queue_avg_minutes ?? 15}
                onChange={(e) => set("queue_avg_minutes", parseInt(e.target.value) || 15)}
                className={inputCls} data-testid="f-queue-avg" /></div>
          )}
            <div><label className={labelCls}>Massimo giorni futuri</label>
              <input type="number" min="0" value={form.max_giorni_futuri} onChange={(e) => set("max_giorni_futuri", e.target.value)} className={inputCls} data-testid="f-max-giorni" /></div>
            <div><label className={labelCls}>Pax minimi</label>
              <input type="number" min="1" value={form.pax_min} onChange={(e) => set("pax_min", e.target.value)} className={inputCls} data-testid="f-pax-min" /></div>
            <div><label className={labelCls}>Pax massimi</label>
              <input type="number" min="1" value={form.pax_max} onChange={(e) => set("pax_max", e.target.value)} className={inputCls} data-testid="f-pax-max" /></div>
          </div>
          <div>
            <label className={labelCls}>Al superamento massimo pax</label>
            <select value={form.on_max_pax} onChange={(e) => set("on_max_pax", e.target.value)} className={inputCls} data-testid="f-on-max-pax">
              <option value="avviso">Mostra un messaggio di avviso</option>
              <option value="blocca">Blocca l'invio del modulo</option>
            </select>
          </div>
          <div><label className={labelCls}>Avviso se superato max pax</label>
            <input value={form.max_pax_message} onChange={(e) => set("max_pax_message", e.target.value)} className={inputCls}
              placeholder="Hai superato il limite massimo di persone prenotabili. Contatta il ristorante." data-testid="f-max-pax-msg" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Max prenotazioni per slot (0 = illimitato)</label>
              <input type="number" min="0" value={form.slot_capacity} onChange={(e) => set("slot_capacity", e.target.value)} className={inputCls} data-testid="f-slot-capacity" /></div>
            <div><label className={labelCls}>Se uno slot non è disponibile</label>
              <select value={form.slot_full} onChange={(e) => set("slot_full", e.target.value)} className={inputCls} data-testid="f-slot-full">
                <option value="nascondi">Nascondi lo slot</option>
                <option value="disabilita">Mostralo disabilitato</option>
              </select></div>
          </div>
          <div>
            <label className={labelCls}>Se non c'è disponibilità quel giorno</label>
            <select value={form.no_availability} onChange={(e) => set("no_availability", e.target.value)} className={inputCls} data-testid="f-no-avail">
              <option value="standard">Mostra avviso standard: "Non ci sono orari disponibili..."</option>
              <option value="custom">Messaggio personalizzato</option>
            </select>
            {form.no_availability === "custom" && (
              <input value={form.no_availability_message} onChange={(e) => set("no_availability_message", e.target.value)}
                className={`${inputCls} mt-2`} placeholder="Scrivi il messaggio..." data-testid="f-no-avail-msg" />
            )}
          </div>
          <div>
            <label className={labelCls}>Lista d'attesa</label>
            <select value={form.waitlist ? "1" : "0"} onChange={(e) => set("waitlist", e.target.value === "1")} className={inputCls} data-testid="f-waitlist">
              <option value="0">Non attiva</option>
              <option value="1">Attiva — slot pieno → prenotazione in lista d'attesa</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Etichetta (visibile sulla prenotazione)</label>
              <input value={form.etichetta} onChange={(e) => set("etichetta", e.target.value)} className={inputCls}
                placeholder="Es. Mare" data-testid="f-etichetta" /></div>
            <div>
              <label className={labelCls}>Tag da applicare al contatto</label>
              <select value={form.tag_name} onChange={(e) => set("tag_name", e.target.value)} className={inputCls} data-testid="f-tag">
                <option value="">Nessun tag</option>
                {tags.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
              <div className="flex gap-1.5 mt-1.5">
                <input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Nuovo tag (es. MARE-PRENOTATO)"
                  className="flex-1 h-9 px-3 rounded-md border border-zinc-300 text-xs bg-white" data-testid="f-new-tag" />
                <button type="button" onClick={createTag} data-testid="f-create-tag"
                  className="h-9 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1">
                  <Tag size={12} /> Crea
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "landing" && (
        <div className="space-y-4" data-testid="panel-landing">
          <div><label className={labelCls}>Titolo *</label>
            <input value={form.landing.titolo} onChange={(e) => setNested("landing", "titolo", e.target.value)} className={inputCls} data-testid="f-landing-titolo" /></div>
          <div><label className={labelCls}>Sottotitolo</label>
            <input value={form.landing.sottotitolo} onChange={(e) => setNested("landing", "sottotitolo", e.target.value)} className={inputCls} data-testid="f-landing-sottotitolo" /></div>
          <div><label className={labelCls}>Descrizione</label>
            <RichTextEditor value={form.landing.descrizione} onChange={(v) => setNested("landing", "descrizione", v)} testid="f-landing-descrizione" /></div>
          <div><label className={labelCls}>Condizioni di prenotazione</label>
            <RichTextEditor value={form.landing.condizioni} onChange={(v) => setNested("landing", "condizioni", v)} testid="f-landing-condizioni" /></div>
          <div>
            <label className={labelCls}>Immagine del modulo</label>
            {form.landing.immagine ? (
              <div className="space-y-2">
                <img src={form.landing.immagine} alt="Anteprima immagine modulo"
                  className="w-full h-36 object-cover rounded-md border border-zinc-200" data-testid="f-landing-immagine-preview" />
                <button type="button" onClick={() => setNested("landing", "immagine", "")} data-testid="f-landing-immagine-remove"
                  className="text-xs text-rose-600 font-semibold">Rimuovi immagine</button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploadingImg} data-testid="f-landing-immagine-upload"
                className="h-10 px-4 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 disabled:opacity-50">
                {uploadingImg ? "Caricamento in corso..." : "+ Carica immagine (JPEG/PNG, max 5 MB)"}
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadLandingImage} data-testid="f-landing-immagine-file" />
          </div>
          {form.landing.immagine && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Come mostrare l'immagine</label>
                <select value={form.landing.immagine_mode || "banner"} onChange={(e) => setNested("landing", "immagine_mode", e.target.value)}
                  className={inputCls} data-testid="f-landing-immagine-mode">
                  <option value="banner">Normale (sopra il titolo)</option>
                  <option value="overlay">Sfondo (dietro le scritte, con velo scuro)</option>
                </select></div>
              <div><label className={labelCls}>Adattamento</label>
                <select value={form.landing.img_fit || "copri"} onChange={(e) => setNested("landing", "img_fit", e.target.value)}
                  className={inputCls} data-testid="f-landing-img-fit">
                  <option value="copri">Riempi lo spazio (taglia i bordi)</option>
                  <option value="intera">Immagine intera (mai tagliata)</option>
                </select>
                <p className="text-[11px] text-zinc-400 mt-1">Formato consigliato: 1600×900 px orizzontale.</p></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Coupon collegato (opzionale)</label>
              <select value={form.coupon_id} onChange={(e) => set("coupon_id", e.target.value)} className={inputCls} data-testid="f-coupon">
                <option value="">Nessun coupon</option>
                {coupons.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {form.coupon_id && (
                <select value={form.coupon_mode || "emetti"} onChange={(e) => set("coupon_mode", e.target.value)}
                  className={inputCls + " mt-2"} data-testid="f-coupon-mode">
                  <option value="emetti">Lo riceve chiunque prenoti</option>
                  <option value="solo_esistenti">Solo chi ce l'ha già (riattivato se scaduto)</option>
                </select>
              )}
              <p className="text-[11px] text-zinc-400 mt-1">{form.coupon_id && form.coupon_mode === "solo_esistenti"
                ? "Nessuna nuova emissione: il coupon del cliente viene agganciato alla prenotazione e riattivato se scaduto."
                : "Il cliente lo riceve automaticamente al termine della prenotazione."}</p>
              <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2" data-testid="f-coupon-rules">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Coupon diverso per numero di persone</div>
                {(form.coupon_rules || []).map((r, i) => (
                  <div key={i} className="rounded-md border border-zinc-200 bg-white p-2.5 space-y-2" data-testid={`f-coupon-rule-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-zinc-500 shrink-0">Da</span>
                      <input type="number" min={1} value={r.pax_min} placeholder="4"
                        onChange={(e) => { const n = [...form.coupon_rules]; n[i] = { ...n[i], pax_min: e.target.value }; set("coupon_rules", n); }}
                        className={inputCls} data-testid={`f-coupon-rule-${i}-min`} />
                      <span className="text-[11px] font-semibold text-zinc-500 shrink-0">a</span>
                      <input type="number" min={1} value={r.pax_max} placeholder="5"
                        onChange={(e) => { const n = [...form.coupon_rules]; n[i] = { ...n[i], pax_max: e.target.value }; set("coupon_rules", n); }}
                        className={inputCls} data-testid={`f-coupon-rule-${i}-max`} />
                      <span className="text-[11px] text-zinc-400 shrink-0">persone</span>
                      <button type="button" onClick={() => set("coupon_rules", form.coupon_rules.filter((_, j) => j !== i))}
                        data-testid={`f-coupon-rule-${i}-remove`} title="Rimuovi fascia"
                        className="ml-auto h-9 w-9 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 shrink-0 flex items-center justify-center">✕</button>
                    </div>
                    <select value={r.coupon_id} onChange={(e) => { const n = [...form.coupon_rules]; n[i] = { ...n[i], coupon_id: e.target.value }; set("coupon_rules", n); }}
                      className={inputCls} data-testid={`f-coupon-rule-${i}-coupon`}>
                      <option value="">— Aggancia un coupon —</option>
                      {coupons.filter((c) => c.tipo !== "vetrina").map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                ))}
                {(form.coupon_rules || []).length < 6 && (
                  <button type="button" data-testid="f-coupon-rule-add"
                    onClick={() => set("coupon_rules", [...(form.coupon_rules || []), { pax_min: 1, pax_max: 5, coupon_id: "" }])}
                    className="w-full h-9 rounded-md border-2 border-dashed border-zinc-300 text-xs font-semibold text-zinc-500 hover:border-zinc-500 hover:text-zinc-700 transition">
                    + Aggiungi fascia coperti
                  </button>
                )}
                <p className="text-[11px] text-zinc-400">Se impostate, il coupon emesso dipende dai coperti prenotati (es. 4-5 → coupon A, 6+ → coupon B). <b>Fuori dalle fasce nessun coupon viene emesso.</b> Lascia vuoto per emettere sempre il coupon collegato sopra.</p>
              </div>
            </div>
            <div><label className={labelCls}>Promozione collegata (opzionale)</label>
              <select value={form.promotion_id} onChange={(e) => set("promotion_id", e.target.value)} className={inputCls} data-testid="f-promo">
                <option value="">Nessuna promozione</option>
                {promos.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-[11px] text-zinc-400 mt-1">Titolo e condizioni della promo compaiono nella landing.</p></div>
          </div>
          <div className="border-t border-zinc-100 pt-4 space-y-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-zinc-700">
              <input type="checkbox" checked={form.landing.solo_landing || false}
                onChange={(e) => setNested("landing", "solo_landing", e.target.checked)}
                className="w-4 h-4" data-testid="f-landing-solo" />
              Solo landing: nascondi il modulo di prenotazione (pagina vetrina con bottoni)
            </label>
            <div className="space-y-2">
              <label className={labelCls}>Bottoni grandi in evidenza (fino a 2)</label>
              {(form.landing.cta_links || []).map((cta, i) => (
                <div key={i} className="grid grid-cols-2 gap-2">
                  <input value={cta.label} placeholder={`Testo bottone ${i + 1} (es. Menù alla carta)`}
                    onChange={(e) => { const next = [...(form.landing.cta_links || [])]; next[i] = { ...next[i], label: e.target.value }; setNested("landing", "cta_links", next); }}
                    className={inputCls} data-testid={`f-cta-label-${i}`} />
                  <input value={cta.url} placeholder="https://… (link del modulo)"
                    onChange={(e) => { const next = [...(form.landing.cta_links || [])]; next[i] = { ...next[i], url: e.target.value }; setNested("landing", "cta_links", next); }}
                    className={inputCls} data-testid={`f-cta-url-${i}`} />
                </div>
              ))}
              <p className="text-[11px] text-zinc-400">Compaiono sotto la descrizione. Con "Solo landing" la pagina mostra solo contenuti e bottoni.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "thankyou" && (
        <div className="space-y-4" data-testid="panel-thankyou">
          <div><label className={labelCls}>Titolo</label>
            <input value={form.thankyou.titolo} onChange={(e) => setNested("thankyou", "titolo", e.target.value)} className={inputCls}
              placeholder="Richiesta di prenotazione inviata con successo!" data-testid="f-ty-titolo" /></div>
          <div><label className={labelCls}>Descrizione</label>
            <RichTextEditor value={form.thankyou.descrizione} onChange={(v) => setNested("thankyou", "descrizione", v)} testid="f-ty-descrizione"
              placeholder="A breve riceverai la conferma dal nostro staff..." /></div>
        </div>
      )}

      {tab === "caparra" && (
        <div className="space-y-4" data-testid="panel-caparra">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="caparra-in-arrivo">
            <b>In arrivo</b>: la configurazione viene salvata ora e sarà attiva sul modulo pubblico quando i pagamenti online (Stripe) saranno abilitati sulla piattaforma.
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-800">
            <input type="checkbox" checked={!!form.caparra.attivo} onChange={(e) => setNested("caparra", "attivo", e.target.checked)}
              className="h-4 w-4 accent-zinc-900" data-testid="f-caparra-attivo" />
            Richiedi caparra per questo modulo
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Importo (€)</label>
              <input type="number" min="0" step="0.5" value={form.caparra.importo} onChange={(e) => setNested("caparra", "importo", e.target.value)} className={inputCls} data-testid="f-caparra-importo" /></div>
            <div><label className={labelCls}>Note</label>
              <input value={form.caparra.note} onChange={(e) => setNested("caparra", "note", e.target.value)} className={inputCls} data-testid="f-caparra-note" /></div>
          </div>
        </div>
      )}

      {tab === "orari" && (
        <div className="space-y-4" data-testid="panel-orari">
          <div>
            <label className={labelCls}>Modalità orari</label>
            <select value={form.orari_mode} onChange={(e) => set("orari_mode", e.target.value)} className={inputCls} data-testid="f-orari-mode">
              <option value="default">Usa gli orari generali del ristorante (Impostazioni → Orari e turni)</option>
              <option value="custom">Orari personalizzati per questo modulo</option>
            </select>
          </div>
          {form.orari_mode === "custom" && (
            <div className="space-y-2" data-testid="f-orari-custom">
              {WEEKDAYS.map(([day, label]) => {
                const d = (form.orari_custom || {})[day] || { aperto: false, turni: [] };
                return (
                  <div key={day} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={`f-day-${day}`}>
                    <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
                      <input type="checkbox" checked={!!d.aperto} className="h-4 w-4 accent-zinc-900" data-testid={`f-day-${day}-aperto`}
                        onChange={(e) => setForm((f) => ({ ...f, orari_custom: { ...(f.orari_custom || {}), [day]: { ...d, aperto: e.target.checked } } }))} />
                      {label}
                    </label>
                    {d.aperto && (
                      <div className="mt-2 space-y-1.5">
                        {(d.turni || []).map((t, i) => (
                          <div key={i} className="flex items-center gap-1.5" data-testid={`f-day-${day}-turno-${i}`}>
                            <input type="time" value={t.da} onChange={(e) => setTurno(day, i, "da", e.target.value)} className="h-9 px-2 rounded-md border border-zinc-300 text-sm bg-white" />
                            <span className="text-xs text-zinc-400">→</span>
                            <input type="time" value={t.a} onChange={(e) => setTurno(day, i, "a", e.target.value)} className="h-9 px-2 rounded-md border border-zinc-300 text-sm bg-white" />
                            <input type="number" min="5" step="5" value={t.slot_minuti} onChange={(e) => setTurno(day, i, "slot_minuti", e.target.value)}
                              className="h-9 w-16 px-2 rounded-md border border-zinc-300 text-sm bg-white" title="Intervallo slot (min)" />
                            <button type="button" onClick={() => setForm((f) => {
                              const oc = { ...(f.orari_custom || {}) };
                              oc[day] = { ...d, turni: (d.turni || []).filter((_, j) => j !== i) };
                              return { ...f, orari_custom: oc };
                            })} className="h-9 w-9 rounded-md bg-white border border-rose-200 text-rose-600 flex items-center justify-center">
                              <Trash size={13} /></button>
                          </div>
                        ))}
                        <button type="button" data-testid={`f-day-${day}-add`}
                          onClick={() => setForm((f) => ({ ...f, orari_custom: { ...(f.orari_custom || {}), [day]: { ...d, turni: [...(d.turni || []), { da: "19:00", a: "23:00", slot_minuti: 30 }] } } }))}
                          className="h-8 px-2.5 rounded-md bg-white border border-zinc-300 text-[11px] font-semibold text-zinc-600 flex items-center gap-1">
                          <Plus size={11} /> Turno
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <button onClick={save} disabled={saving} data-testid="editor-save"
        className="w-full h-12 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 active:scale-[0.98] transition disabled:opacity-50 flex items-center justify-center gap-2">
        <FloppyDisk size={16} weight="bold" /> {saving ? "Salvataggio..." : "Salva modulo"}
      </button>
    </div>
  );
}
